import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Mail, User } from "lucide-react";
import { ThemeToggle } from "./ThemeToggle";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/service-area", label: "Service Area" },
  { to: "/gallery", label: "Gallery" },
  { to: "/reviews", label: "Reviews" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

function Logo() {
  return (
    <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5">
      <img
        src="/logo-bins-plus.png"
        alt="Bins Plus Pressure Washing"
        className="h-12 w-12 object-contain drop-shadow-[0_0_10px_rgba(30,144,255,0.35)]"
      />
      <span className="hidden font-heading text-base font-extrabold leading-tight tracking-tight text-foreground sm:block">
        Bins Plus <span className="text-secondary">Pressure Washing</span>
      </span>
    </Link>
  );
}

export function Navbar() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const { user } = useAuth();
  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-xl">
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 lg:px-8">
        <Logo />
        <div className="hidden items-center gap-0.5 lg:flex">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) =>
                `rounded-lg px-2.5 py-2 text-sm font-semibold transition-colors ${
                  isActive
                    ? "text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          {user ? (
            <Link
              to={user.role === "admin" ? "/admin/dashboard" : "/account"}
              data-testid="nav-account"
              className="hidden items-center gap-1.5 rounded-xl border border-border px-4 py-2.5 text-sm font-bold transition-colors hover:bg-accent sm:inline-flex"
            >
              <User className="h-4 w-4" /> {user.role === "admin" ? "Dashboard" : "Account"}
            </Link>
          ) : (
            <Link
              to="/login"
              data-testid="nav-login"
              className="hidden rounded-xl border border-border px-4 py-2.5 text-sm font-bold transition-colors hover:bg-accent sm:inline-flex"
            >
              Log in
            </Link>
          )}
          <Link
            to="/booking"
            data-testid="nav-book-now"
            className="hidden rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground shadow-sm transition-all hover:-translate-y-0.5 hover:brightness-110 sm:inline-flex"
          >
            Book Now
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border lg:hidden"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>
      {open && (
        <div className="border-t border-border bg-background px-5 py-3 lg:hidden">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              onClick={() => setOpen(false)}
              className={`block rounded-lg px-3 py-3 text-base font-semibold ${
                loc.pathname === n.to ? "text-primary" : "text-foreground"
              }`}
            >
              {n.label}
            </Link>
          ))}
          <Link
            to={user ? (user.role === "admin" ? "/admin/dashboard" : "/account") : "/login"}
            onClick={() => setOpen(false)}
            className="block rounded-lg px-3 py-3 text-base font-semibold text-foreground"
          >
            {user ? (user.role === "admin" ? "Dashboard" : "My Account") : "Log in"}
          </Link>
          <Link
            to="/booking"
            onClick={() => setOpen(false)}
            className="mt-2 block rounded-xl bg-primary px-5 py-3 text-center text-base font-bold text-primary-foreground"
          >
            Book Now
          </Link>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-border bg-card">
      <div className="mx-auto grid max-w-7xl gap-8 px-5 py-14 lg:grid-cols-4 lg:px-8">
        <div>
          <Link to="/" data-testid="footer-wordmark" className="inline-block">
            <img
              src="/wordmark-bins-plus.png"
              alt="Bins Plus Pressure Washing — More Than Bins."
              className="h-16 w-auto object-contain drop-shadow-[0_0_15px_rgba(57,255,20,0.12)]"
            />
          </Link>
          <p className="mt-4 max-w-xs text-sm text-muted-foreground">
            Curbside bin cleaning & pressure washing for Southern Maryland.
            Fresh, sanitized, odor-free — every visit.
          </p>
        </div>
        <div>
          <h4 className="mb-3 text-sm font-bold uppercase tracking-wider text-foreground">
            Company
          </h4>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li><Link to="/services" className="hover:text-primary">Services</Link></li>
            <li><Link to="/how-it-works" className="hover:text-primary">How It Works</Link></li>
            <li><Link to="/service-area" className="hover:text-primary">Service Area</Link></li>
            <li><Link to="/faq" className="hover:text-primary">FAQ</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="mb-3 text-sm font-bold uppercase tracking-wider text-foreground">
            Get Started
          </h4>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li><Link to="/booking" className="hover:text-primary">Book a Cleaning</Link></li>
            <li><Link to="/manage" className="hover:text-primary">Manage Subscription</Link></li>
            <li><Link to="/contact" className="hover:text-primary">Contact Us</Link></li>
            <li><Link to="/admin" className="hover:text-primary">Owner Login</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="mb-3 text-sm font-bold uppercase tracking-wider text-foreground">
            Contact
          </h4>
          <a
            href="mailto:ogmoss@renewmybins.com"
            target="_top"
            onClick={() => { navigator.clipboard.writeText("ogmoss@renewmybins.com").then(() => toast.success("Email copied: ogmoss@renewmybins.com")).catch((err) => { if (process.env.NODE_ENV === "development") console.error("Clipboard copy failed:", err); }); }}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"
          >
            <Mail className="h-4 w-4" /> ogmoss@renewmybins.com
          </a>
          <p className="mt-3 text-sm text-muted-foreground">
            Serving Charles, Calvert & Southern PG County.
          </p>
        </div>
      </div>
      <div className="border-t border-border py-5 text-center text-xs text-muted-foreground">
        © {new Date().getFullYear()} Bins Plus Pressure Washing · <span className="italic">More Than Bins.</span> All rights reserved.
      </div>
    </footer>
  );
}

export default function PublicLayout({ children }) {
  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
